'use client'

import { useState, useEffect } from 'react'
import Footer from '@/components/Footer'
import SuccessNotification from '@/components/SuccessNotification'

// Generate a simple CSRF token (in production, use a more secure method)
function generateCsrfToken(): string {
  const array = new Uint8Array(32)
  if (typeof window !== 'undefined' && window.crypto) {
    window.crypto.getRandomValues(array)
  } else {
    // Fallback for environments without crypto
    for (let i = 0; i < array.length; i++) {
      array[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('')
}

export default function Contact() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [prompt, setPrompt] = useState('')
  const [website, setWebsite] = useState('') // Honeypot field for bot prevention
  const [csrfToken, setCsrfToken] = useState<string>('')
  const [submitted, setSubmitted] = useState(false)
  const [selectedExample, setSelectedExample] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [validationErrors, setValidationErrors] = useState<{
    name?: string
    email?: string
    prompt?: string
  }>({})
  const [successMessage, setSuccessMessage] = useState<string>('')

  // Generate CSRF token on component mount
  useEffect(() => {
    setCsrfToken(generateCsrfToken())
  }, [])

  const examplePrompts = [
    "Designing a self-service platform that removes operational friction",
    "Untangling a system that has grown hard to reason about",
    "Building an editorial or internal tool that prioritizes signal over noise",
    "Creating a modular architecture that can evolve without breaking itself"
  ]

  const handleExampleClick = (example: string) => {
    setPrompt(example)
    setSelectedExample(example)
    setValidationErrors({})
  }

  const validateForm = (): boolean => {
    const errors: { name?: string; email?: string; prompt?: string } = {}
    let isValid = true

    // Name validation
    const trimmedName = name.trim()
    if (!trimmedName || trimmedName.length < 2) {
      errors.name = 'Name must be at least 2 characters long'
      isValid = false
    } else if (trimmedName.length > 100) {
      errors.name = 'Name is too long'
      isValid = false
    }

    // Email validation
    const trimmedEmail = email.trim()
    if (!trimmedEmail) {
      errors.email = 'Email is required'
      isValid = false
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(trimmedEmail)) {
        errors.email = 'Invalid email format'
        isValid = false
      } else if (trimmedEmail.length > 255) {
        errors.email = 'Email is too long'
        isValid = false
      }
    }

    // Prompt validation
    const trimmedPrompt = prompt.trim()
    if (!trimmedPrompt || trimmedPrompt.length < 10) {
      errors.prompt = 'Please provide more details (at least 10 characters)'
      isValid = false
    } else if (trimmedPrompt.length > 5000) {
      errors.prompt = 'Message is too long (max 5000 characters)'
      isValid = false
    }

    setValidationErrors(errors)
    return isValid
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setValidationErrors({})

    // Frontend validation
    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          prompt: prompt.trim(),
          website: website.trim(), // Honeypot field
          csrfToken: csrfToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message')
      }

      // Success
      setSuccessMessage(data.message || 'Thank you for your message. I\'ll review it and respond if this feels like a good fit.')
      setSubmitted(true)
      setName('')
      setEmail('')
      setPrompt('')
      setWebsite('')
      setSelectedExample(null)

    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg-primary relative">
      {/* Success Notification */}
      {submitted && successMessage && (
        <SuccessNotification
          message={successMessage}
          onClose={() => {
            setSubmitted(false)
            setSuccessMessage('')
          }}
          duration={6000}
        />
      )}

      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">

          {!submitted && (
            <form onSubmit={handleSubmit} className="relative">
              
              {/* Opening Section */}
              <div className="space-y-5">
                <p className="text-sm text-text-muted font-mono">
                  Let's start simple.
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <input
                    type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value)
                        if (validationErrors.name) {
                          setValidationErrors(prev => ({ ...prev, name: undefined }))
                        }
                      }}
                      className={`w-full px-4 py-3 bg-bg-elevated border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent-primary/30 focus:border-accent-primary/50 text-text-primary placeholder-text-muted font-light text-base transition-all duration-150 ${
                        validationErrors.name ? 'border-red-500/50' : 'border-border-subtle'
                      }`}
                      placeholder="Hi, I'm…"
                    required
                  />
                    {validationErrors.name && (
                      <p className="text-xs text-red-400/80 mt-1.5 ml-1">{validationErrors.name}</p>
                    )}
                </div>
                <div>
                  <input
                    type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        if (validationErrors.email) {
                          setValidationErrors(prev => ({ ...prev, email: undefined }))
                        }
                      }}
                      className={`w-full px-4 py-3 bg-bg-elevated border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent-primary/30 focus:border-accent-primary/50 text-text-primary placeholder-text-muted font-light text-base transition-all duration-150 ${
                        validationErrors.email ? 'border-red-500/50' : 'border-border-subtle'
                      }`}
                      placeholder="You can reach me at…"
                      required
                    />
                    {validationErrors.email && (
                      <p className="text-xs text-red-400/80 mt-1.5 ml-1">{validationErrors.email}</p>
                    )}
                  </div>
                </div>
                
                {/* Honeypot field - hidden from humans, visible to bots */}
                <input
                  type="text"
                  name="website"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  style={{
                    position: 'absolute',
                    left: '-9999px',
                    opacity: 0,
                    pointerEvents: 'none',
                  }}
                  aria-hidden="true"
                />
              </div>

              {/* Transition Divider */}
              <div className="relative pt-6">
                <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
              </div>

              {/* Main Prompt Section */}
              <div className="space-y-6 pt-4">
                <div>
                  <h2 className="text-3xl sm:text-4xl font-light text-text-primary mb-3">
                    Ready to build?
                  </h2>
                  <p className="text-sm text-text-muted max-w-2xl leading-relaxed mb-3">
                    Describe the system you want to build — or the one you're trying to fix.
                  </p>
                  <p className="text-xs text-text-muted leading-relaxed">
                    This doesn't need to be perfect. Just clear enough to begin.
                  </p>
                </div>

                {/* Prompt Starters */}
                <div className="space-y-4">
                  <p className="text-xs text-text-muted font-mono uppercase tracking-wider">
                    Not sure how to start? Try one of these.
                  </p>
                  <div className="grid grid-cols-1 gap-3">
                    {examplePrompts.map((example, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleExampleClick(example)}
                        className={`text-left px-4 py-3 bg-bg-elevated border rounded-sm transition-all duration-150 hover:bg-bg-secondary hover:border-accent-primary/30 ${
                          selectedExample === example
                            ? 'border-accent-primary/50 bg-bg-secondary'
                            : 'border-border-subtle'
                        }`}
                      >
                        <p className="text-sm text-text-secondary leading-relaxed font-light">
                          {example}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Main Text Area */}
                <div className="space-y-3">
                  <label className="block text-sm text-text-muted font-mono">
                    Tell me about the system
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => {
                      setPrompt(e.target.value)
                      setSelectedExample(null)
                      if (validationErrors.prompt) {
                        setValidationErrors(prev => ({ ...prev, prompt: undefined }))
                      }
                    }}
                    className={`w-full min-h-[280px] px-4 py-4 bg-bg-elevated border rounded-sm focus:outline-none focus:ring-1 focus:ring-accent-primary/30 focus:border-accent-primary/50 text-text-primary placeholder-text-muted resize-none font-light text-base leading-relaxed transition-all duration-150 ${
                      validationErrors.prompt ? 'border-red-500/50' : 'border-border-subtle'
                    }`}
                    placeholder="What exists today?&#10;What's breaking?&#10;What needs to age well?"
                    required
                  />
                  {validationErrors.prompt && (
                    <p className="text-xs text-red-400/80 mt-1.5 ml-1">{validationErrors.prompt}</p>
                  )}
                </div>

                {/* Human Assurance */}
                <div className="pt-2">
                  <p className="text-xs text-text-muted leading-relaxed">
                    I read every message myself. I typically respond within 48 hours.
                    <br />
                    If this feels like a good fit, I'll reply with next steps.
                  </p>
              </div>

                {/* Error Message */}
                {error && (
                  <div className="pt-2">
                    <p className="text-sm text-red-400/80 leading-relaxed">
                      {error}
                    </p>
                  </div>
                )}

                {/* Primary CTA */}
                <div className="pt-6">
                <button
                  type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-accent-primary/10 border border-accent-primary/30 text-accent-primary rounded-sm hover:bg-accent-primary/20 hover:border-accent-primary/50 transition-all duration-150 text-sm font-mono font-light disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSubmitting ? 'Sending...' : 'Start the conversation →'}
                </button>
                  <p className="text-xs text-text-muted/70 mt-3 leading-relaxed">
                    No pressure. Just clarity.
                  </p>
                </div>
              </div>

            </form>
          )}
        </div>
      </div>
      
      {/* Footer */}
      <Footer maxWidth="max-w-3xl" />
    </div>
  )
}
